import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Badge, Button, Detail } from '../../components/ui'
import type {
  BlockingSpace,
  Listing,
  PropertyCard,
  PropertyContactLink,
  Unit,
} from '../../api/objects'
import { createListing } from '../../api/objects-write'
import { EditPropertyForm } from './EditPropertyForm'
import { useSession } from '../../auth/session'
import { t } from '../../i18n'
import { fullTime } from '../../format'
import { propertyRoleLabel, unitKindLabel } from '../../labels'
import { AddUnitForm } from './AddUnitForm'
import { DeleteProperty, DeleteSpace } from './DeleteButton'
import { EditUnitForm } from './EditUnitForm'
import { Explain } from './Explain'
import { AvailabilityForm } from './AvailabilityForm'
import { ListingCard } from './ListingCard'
import { TermsForm } from './TermsForm'
import { MediaPanel } from './MediaPanel'
import { OfferSwitch } from './OfferSwitch'
import { PriceForm } from './PriceForm'
import { PriceTable } from './PriceTable'

/**
 * One property, its units, and the offers on each unit.
 *
 * The nesting is the domain and the screen is built to make it obvious. A room
 * is not a detail of a flat: it is a unit in its own right, with its own
 * capacity, its own listings and its own price, and it sits beside the whole-flat
 * unit rather than inside it. Flattening that is how a room ends up inheriting a
 * flat's rent.
 */
export function PropertyView({
  card,
  now,
}: {
  readonly card: PropertyCard
  /** Injected by the checks so that "the check expired" does not depend on the day. */
  readonly now?: Date | undefined
}) {
  const { property, contacts, units } = card
  const { can } = useSession()
  const address = property.addressExact === '' ? property.addressNormalized : property.addressExact

  return (
    <div className="property">
      <header className="property__head">
        <h1 className="property__address">{address === '' ? t('objects.addressUnknown') : address}</h1>
        <div className="property__badges">
          {property.city === '' ? null : <Badge tone="muted">{property.city}</Badge>}
          {property.area === '' ? null : <Badge tone="neutral">{property.area}</Badge>}
        </div>
      </header>

      {/*
        Some publishers withhold the exact address until a viewing is agreed. That
        is a fact about the source, not a gap in the import, so it is said rather
        than left as an address that looks approximate for no reason.
      */}
      {property.addressExact === '' ? (
        <p className="property__note">{t('objects.addressExactHidden')}</p>
      ) : null}

      <dl className="details">
        {property.municipality === '' ? null : (
          <Detail term={t('objects.municipality')}>{property.municipality}</Detail>
        )}
        <Detail term={t('objects.floor')}>
          {property.floor === undefined ? <NotAsked /> : property.floor}
        </Detail>
        {/*
          A fourth floor with no lift is a reason a client refuses a flat. Nobody
          having asked and there being no lift are different answers, and only one
          of them is worth a phone call.
        */}
        <Detail term={t('objects.elevator')}>
          {property.elevator === undefined ? (
            <NotAsked />
          ) : property.elevator ? (
            t('common.yes')
          ) : (
            t('common.no')
          )}
        </Detail>
        {property.heating === '' ? null : (
          <Detail term={t('objects.heating')}>{property.heating}</Detail>
        )}
      </dl>

      {property.notes === '' ? null : <p className="property__note">{property.notes}</p>}

      {/*
        Correcting the address after the fact. Without it the only way to fix a
        typo in a street was to delete the object and enter it again, losing its
        photographs, its prices and every check recorded against it.
      */}
      <EditPropertyForm property={property} />

      <section className="panel-block">
        <h2 className="panel-block__title">{t('owners.title')}</h2>
        {contacts.length === 0 ? (
          <p className="panel-block__empty">{t('owners.empty')}</p>
        ) : (
          <ul className="owners">
            {contacts.map((contact) => (
              <OwnerRow key={contact.id} contact={contact} />
            ))}
          </ul>
        )}
      </section>

      <section className="panel-block">
        <div className="panel-block__head">
          <h2 className="panel-block__title">{t('objects.whatIsLet')}</h2>
          <Badge tone="muted">{t('objects.unitCount', { count: units.length })}</Badge>
        </div>
        <p className="panel-block__hint">
          {units.length === 0 ? t('objects.whatIsLetHint') : t('objects.oneObjectHint')}
        </p>
        <Explain>{t('objects.whatIsLetWhy')}</Explain>

        {units.length === 0 ? (
          <p className="panel-block__empty">{t('objects.unitsEmpty')}</p>
        ) : (
          // Only the spaces nothing contains are drawn here; each one draws what
          // is inside it. A flat list would put a room and the flat it is in
          // beside each other, and the thing worth seeing about them is that
          // letting one stops the other (docs/adr/0010).
          units
            .filter((unit) => unit.parentUnitId === undefined)
            .map((unit) => (
              <UnitBlock
                key={unit.id}
                propertyId={property.id}
                unit={unit}
                units={units}
                now={now}
              />
            ))
        )}

        {/* Three, because there are three things somebody may hold at an address
            and no way to guess which.

            A previous version had one button here and made everybody start with
            a flat, on the reasoning that a room is always inside one. That is
            true about the bricks and false about the business: what is inside
            what is a fact about the building, and what we keep here is what we
            may let. When all we may let is a room in a flat we have nothing to
            do with, making somebody invent the flat is making them record a
            thing they do not have.

            The labels say "отдельную" where the nested buttons say "+ Комната",
            because two buttons reading the same and meaning different things is
            the confusion this is fixing rather than repeating. */}
        {/* Only while the card is empty. One card is one thing being let -- a
            flat, or a room, or a place -- and everything after that goes inside
            it, on the buttons that sit on the space itself. Offering these again
            afterwards would offer a second flat at the same address, which is
            two objects and belongs on two cards. */}
        {!can('listings.write') || units.length > 0 ? null : (
          <div className="panel-block__actions">
            <span className="panel-block__label">{t('build.addHere')}</span>
            <AddUnitForm propertyID={property.id} kind="apartment" standalone />
            <AddUnitForm propertyID={property.id} kind="room" standalone />
            <AddUnitForm propertyID={property.id} kind="bed" standalone />
          </div>
        )}
      </section>

      {/* At the bottom and on its own: it takes every space, every price and
          every photograph at this address, and is not a thing to leave under
          the cursor while somebody reads the card. */}
      {!can('listings.write') ? null : (
      <section className="panel-block panel-block--quiet">
        <DeleteProperty
          propertyID={property.id}
          address={property.addressExact === '' ? property.addressNormalized : property.addressExact}
          units={units}
        />
      </section>
      )}
    </div>
  )
}

function NotAsked() {
  return <span className="not-asked">{t('unit.notAsked')}</span>
}

function OwnerRow({ contact }: { readonly contact: PropertyContactLink }) {
  return (
    <li className="owner">
      <span className="owner__role">{propertyRoleLabel(contact.role)}</span>
      <span className="owner__name">
        {contact.displayName === '' ? contact.contactId : contact.displayName}
      </span>
      {/*
        Specification §16.3 has agents presenting themselves as owners. The answer
        is to record the claim, not to believe it, so an unconfirmed role says so
        instead of reading like an established fact.
      */}
      {contact.confirmedAt === undefined ? (
        <Badge tone="warning">{t('owners.claimed')}</Badge>
      ) : (
        <span className="owner__confirmed">
          {t('owners.confirmed', { time: fullTime(contact.confirmedAt) })}
        </span>
      )}
      {contact.note === '' ? null : <span className="owner__note">{contact.note}</span>}
    </li>
  )
}

export function UnitBlock({
  propertyId,
  unit,
  units,
  now,
}: {
  readonly propertyId: string
  readonly unit: Unit
  /** Every space at this address, so a block can find what is inside it. */
  readonly units: readonly Unit[]
  readonly now?: Date | undefined
}) {
  const { can } = useSession()
  const inside = units.filter((candidate) => candidate.parentUnitId === unit.id)
  // The one offer a space carries in the ordinary case. More than one is a
  // deliberate act and is drawn the long way below.
  const only = unit.listings.length === 1 ? unit.listings[0] : undefined

  return (
    <section className="unit">
      <header className="unit__head">
        <h3 className="unit__title">{unit.label === '' ? unitKindLabel(unit.kind) : unit.label}</h3>
        <Badge tone={unit.kind === 'room' ? 'accent' : 'neutral'}>{unitKindLabel(unit.kind)}</Badge>
      </header>

      {unit.blockedBy.length === 0 ? null : (
        <div className="unit__blocked">
          <p className="unit__blocked-line">
            {t('offer.blocked', { what: unit.blockedBy.map(nameOf).join(', ') })}
          </p>
          <Explain>{t('offer.blockedWhy')}</Explain>
        </div>
      )}

      <dl className="details details--tight">
        {unit.areaSqm === undefined ? null : (
          <Detail term={t('build.areaSqm')}>{unit.areaSqm}</Detail>
        )}
        {/* A room has no bedrooms and a place has no bathrooms. Showing an
            empty row for a question that does not apply is how a screen tells
            somebody it does not understand what they are looking at. */}
        {unit.bedrooms === undefined || unit.kind !== 'apartment' ? null : (
          <Detail term={t('unit.bedrooms')}>{unit.bedrooms}</Detail>
        )}
        {unit.beds === undefined ? null : <Detail term={t('unit.beds')}>{unit.beds}</Detail>}
        {unit.bathrooms === undefined || unit.kind !== 'apartment' ? null : (
          <Detail term={t('unit.bathrooms')}>{unit.bathrooms}</Detail>
        )}
        {/*
          Capacity as the owner states it. Unknown is shown as unknown because the
          matcher may not compare against it either: "more confirmed occupants
          than the flat holds" is a hard filter, and an unknown capacity has to
          reach a person rather than silently pass or silently fail.
        */}
        <Detail term={t('unit.capacity')}>
          {unit.capacity === undefined ? (
            <span className="not-asked">{t('unit.capacityUnknown')}</span>
          ) : (
            unit.capacity
          )}
        </Detail>
        {unit.kind === 'room' ? (
          <Detail term={t('unit.roomPrivate')}>
            {unit.roomPrivate === undefined ? (
              <NotAsked />
            ) : unit.roomPrivate ? (
              t('common.yes')
            ) : (
              t('common.no')
            )}
          </Detail>
        ) : null}
        <Detail term={t('unit.furnished')}>
          {unit.furnished === undefined ? (
            <NotAsked />
          ) : unit.furnished ? (
            t('common.yes')
          ) : (
            t('common.no')
          )}
        </Detail>
      </dl>

      {unit.notes === '' ? null : <p className="unit__note">{unit.notes}</p>}

      {/* The space is the thing on screen and its offer is drawn on it, because
          "offer" is our data model and not a word anybody letting their own flat
          uses. A space carries exactly one until somebody deliberately adds a
          second -- a different price for the winter -- and only then is there
          anything worth separating. */}
      {only === undefined ? null : (
        <UnitOffer propertyId={propertyId} listing={only} blockedBy={unit.blockedBy} now={now} />
      )}

      {unit.listings.length > 1 ? (
        <div className="unit__listings">
          {unit.listings.map((listing) => (
            <ListingCard key={listing.id} propertyId={propertyId} listing={listing} now={now} />
          ))}
        </div>
      ) : null}

      {/* Everything that changes the inventory is behind the permission that says
          so. The server refuses without it either way, but a screen offering a
          button that answers 403 is a screen teaching somebody to distrust its
          buttons. */}
      {!can('listings.write') ? null : (
      <div className="unit__actions">
        <EditUnitForm unit={unit} propertyID={propertyId} />
        <DeleteSpace unit={unit} units={units} propertyID={propertyId} />
        {/* What may go inside this one. A flat holds rooms, a room holds places,
            and a place holds nothing. */}
        {unit.kind === 'apartment' ? (
          <>
            <AddUnitForm propertyID={propertyId} kind="room" parentUnitID={unit.id} />
            {/* The mistake worth heading off: somebody adds rooms to describe the
                flat -- it has three of them -- and quietly creates three things
                the assistant may offer separately. How many rooms a flat has is a
                number in its own fields; a room here is a room we let on its
                own. */}
            <Explain>{t('build.whenToAddRoom')}</Explain>
          </>
        ) : null}
        {unit.kind === 'room' ? (
          <AddUnitForm propertyID={propertyId} kind="bed" parentUnitID={unit.id} />
        ) : null}
        {/* Only where there is already one, so the ordinary case never sees the
            word. */}
        {unit.listings.length === 0 ? (
          <NewListingButton unitID={unit.id} propertyID={propertyId} />
        ) : null}
      </div>
      )}

      {inside.length === 0 ? null : (
        <div className="unit__inside">
          {inside.map((child) => (
            <UnitBlock
              key={child.id}
              propertyId={propertyId}
              unit={child}
              units={units}
              now={now}
            />
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * Putting an offer on a space.
 *
 * A title and nothing else. The price, the availability and the conditions are
 * added afterwards in the offer's own card, where a person can see what they
 * already recorded — and where an empty field is visibly empty rather than
 * something they hurried past on the way to saving.
 */
function NewListingButton({
  unitID,
  propertyID,
}: {
  readonly unitID: string
  readonly propertyID: string
}) {
  const [title, setTitle] = useState<string | null>(null)
  const client = useQueryClient()

  const create = useMutation({
    mutationFn: () => createListing(unitID, { title: (title ?? '').trim() }),
    onSuccess: () => {
      setTitle(null)
      void client.invalidateQueries({ queryKey: ['property', propertyID] })
    },
  })

  if (title === null) {
    return (
      <Button variant="secondary" onClick={() => setTitle('')}>
        + {t('build.addListing')}
      </Button>
    )
  }

  return (
    <form
      className="build-form build-form--inline"
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate()
      }}
    >
      <div className="field">
        <label className="field__label" htmlFor={`listing-title-${unitID}`}>
          {t('build.title')}
        </label>
        <input
          id={`listing-title-${unitID}`}
          className="field__input"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          autoFocus
        />
      </div>
      <div className="build-form__actions">
        <Button type="submit" busy={create.isPending}>
          {create.isPending ? t('build.creating') : t('build.create')}
        </Button>
        <Button variant="secondary" onClick={() => setTitle(null)} disabled={create.isPending}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

/**
 * A space's price, its photographs and whether we are offering it.
 *
 * What is deliberately not here: the verification panel, the freshness line and
 * the change history. All three were built for listings held from somebody else,
 * where a flat can be let out from under us and a check really does go stale.
 * The agency lets its own flats, so the person reading this screen is the source
 * of truth about them, and asking them to prove their own flat exists was the
 * panel arguing with the only witness there is. The history is still recorded
 * and still readable; it is simply not the first thing anybody needs.
 */
function UnitOffer({
  propertyId,
  listing,
  blockedBy,
  now,
}: {
  readonly propertyId: string
  readonly listing: Listing
  readonly blockedBy: readonly BlockingSpace[]
  readonly now?: Date | undefined
}) {
  const { can } = useSession()
  void now

  return (
    <div className="unit__offer">
      {can('listings.write') ? (
        <OfferSwitch
          listingID={listing.id}
          status={listing.status}
          propertyID={propertyId}
          blocked={blockedBy.map(nameOf)}
        />
      ) : null}

      <PriceTable components={listing.priceComponents} />

      {can('listings.write') ? (
        <>
          <PriceForm
            listingID={listing.id}
            propertyID={propertyId}
            components={listing.priceComponents}
          />
          {/*
            The two things that decide whether this flat can ever be offered, and
            both were unreachable from here: a space with one offer renders this
            card, and the availability form lived only in ListingCard, which is
            drawn solely when a space has several. Every ordinary flat therefore
            had no way to say when it is free and no way to say what contract it
            is let on — and the matcher refuses both.
          */}
          <AvailabilityForm
            listingID={listing.id}
            propertyID={propertyId}
            windows={listing.availabilityWindows}
          />
          <TermsForm listing={listing} propertyID={propertyId} />
          <MediaPanel listingID={listing.id} propertyID={propertyId} assets={listing.media} />
        </>
      ) : null}
    </div>
  )
}

/** What to call a space that is in the way, falling back to its kind. */
function nameOf(space: { readonly kind: string; readonly label: string }): string {
  if (space.label !== '') return `«${space.label}»`
  return unitKindLabel(space.kind).toLowerCase()
}
